import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  Optional,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {ComboBoxComponent} from '../combo-box/combo-box.component';
import {ControlValueAccessor, NgControl} from '@angular/forms';
import {ComboBoxDropdownOptionComponent} from '../combo-box-dropdown/combo-box-dropdown.component';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';

interface Country {
  isocode: string;
  name: string;
}

@Component({
  selector: 'ds-country-selector',
  templateUrl: './country-selector.component.html',
  styleUrls: ['./country-selector.component.scss']
})
export class CountrySelectorComponent implements AfterViewInit, ControlValueAccessor, OnDestroy {

  constructor(@Optional() public control: NgControl) {
    if (this.control) {
      (this.control.valueAccessor = this);
    }
  }

  get value(): string {
    return this.innerValue[0];
  }

  set value(value: string) {
    if (value) {
      this.initialCountry = value;
      this.innerValue = [value];
      if (this.onChangeCallback) {
        this.onChangeCallback(value);
      }
    }
  }

  /**
   * A list of countries with isocode and name
   */
  @Input() countryList: Country[] = [];

  /**
   * @ignore
   */
  @Input() label: string;
  @Input() searchPlaceholder: string;
  @Input() initialCountry: string;
  @ViewChild(ComboBoxComponent, {static: true}) innerComboBox: ComboBoxComponent;
  @ViewChildren(ComboBoxDropdownOptionComponent) innerComboBoxDropdown: QueryList<ComboBoxDropdownOptionComponent>;
  innerValue: string[] = [''];
  public selectedHtml: string;
  private onTouchedCallback: () => {};
  private onChangeCallback: (_: any) => {};

  destroy$: Subject<boolean> = new Subject<boolean>();
  isDisabled: boolean = false;

  /**
   * Removes the strong tag from the html code.
   *
   * @param html html code to remove the strong tag from.
   */
  static removeStrongTag(html: string): string {
    if (html) {
      return html.replace('<strong>', '')
        .replace('</strong>', '');
    }
    return undefined;
  }

  /**
   * Updates the this.value when the innerComboBox is changed.
   */
  ngAfterViewInit(): void {
    if (this.innerComboBox) {
      this.innerComboBox.valueChange.pipe(
        takeUntil(this.destroy$)
      ).subscribe((value: string[]) => {
        if (value) {
          this.value = value[0];
        }
      });
    }
    setTimeout(() => {
      this.setSelectedCountry(this.initialCountry);
      if(this.initialCountry && this.initialCountry !== this.innerValue[0]) {
        this.value = this.initialCountry;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  /**
   * Reacts on [(ngModel)] changes.
   *
   * @param value value passed into [(ngModel)]
   */
  writeValue(value: string) {
    if (value && value !== this.value) {
      this.value = value;
      this.setSelectedCountry(this.value);
    }
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  changeFlag($event: string) {
    this.setSelectedCountry($event);
  }

  /**
   * Finds the html code of the country selected value and places it in the combobox component.
   *
   * @param isocode of the selected country
   */
  setSelectedCountry(isocode: string) {
    const selectedHtml = this.getSelectedCountry(isocode);
    this.selectedHtml = CountrySelectorComponent.removeStrongTag(selectedHtml);
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * Finds the html code of the country selected value and places it in the combobox component.
   *
   * @param isocode of the selected country
   */
  getSelectedCountry(isocode: string): string {
    if (this.innerComboBoxDropdown) {
      const lcs: ComboBoxDropdownOptionComponent[] = this.innerComboBoxDropdown.filter(ic => ic.value === isocode);
      if (lcs[0] && lcs[0].element && lcs[0].element.nativeElement) {
        return lcs[0].element.nativeElement.innerHTML;
      }
    }
  }

  public focus(): void {
    this.innerComboBox.focus();
  }
}
